import React from "react";
import Skeleton from "react-loading-skeleton";
import { BackIcon, EditIcon } from "../../assets/Icons";
import { getInitials } from "../../utils/util";
import { scrollAppToTop } from "../../utils/scrollToTop";

const EntityInfoV2View = ({
  navigate,
  loading,
  entityName,
  editPath,
  thumbnail,
  responseCount,
  questionCount,
  serialNo,
  entityIdLabel,
  entityId,
  visibleGroups = [],
  hiddenGroups = [],
  tags = [],
  kpiCards = [],
  tabs = [],
  activeTab,
  onTabChange,
  tabActions,
  children,
}) => {
  const [showAllGroups, setShowAllGroups] = React.useState(false);
  const allGroups = [...visibleGroups, ...hiddenGroups];

  return (
    <div className="entity-info-v2">
      <button type="button" className="entity-info-v2__back" onClick={() => navigate(-1)}>
        <BackIcon />
        Back
      </button>

      <div className="entity-info-v2__summary">
        <div className="entity-info-v2__card entity-info-v2__card--profile">
          <div className="entity-info-v2__avatar">
            {thumbnail ? (
              <img src={thumbnail} alt="" />
            ) : (
              <span>{getInitials(entityName)}</span>
            )}
          </div>
          <div className="entity-info-v2__title-row">
            {loading ? (
              <Skeleton width={220} height={28} />
            ) : (
              <>
                <h1>{entityName}</h1>
                {editPath && (
                  <button type="button" className="entity-info-v2__edit" onClick={() => navigate(editPath)}>
                    <EditIcon />
                  </button>
                )}
              </>
            )}
          </div>
          <div className="entity-info-v2__stats">
            <div className="entity-info-v2__stat-item">
              <strong>{responseCount}</strong>
              <span>User response received</span>
            </div>
            <div className="entity-info-v2__stats-divider" />
            <div className="entity-info-v2__stat-item">
              <strong>{questionCount}</strong>
              <span>Questions</span>
            </div>
          </div>
        </div>

        <div className="entity-info-v2__card entity-info-v2__card--details">
          <div className="entity-info-v2__detail-row">
            <div className="entity-info-v2__detail-item">
              <span>Serial Number</span>
              {loading ? (
                <Skeleton width={100} height={20} />
              ) : (
                <strong>{serialNo || "NA"}</strong>
              )}
            </div>
            <div className="entity-info-v2__detail-item">
              <span>{entityIdLabel}</span>
              {loading ? (
                <Skeleton width={100} height={20} />
              ) : (
                <strong>{entityId || "—"}</strong>
              )}
            </div>
          </div>

          <div className="entity-info-v2__detail-block">
            <span>Group</span>
            {loading ? (
              <div className="entity-info-v2__group-pills">
                <Skeleton width={90} height={32} borderRadius={999} />
                <Skeleton width={90} height={32} borderRadius={999} />
              </div>
            ) : (
              <div className="entity-info-v2__group-pills">
                {(showAllGroups ? allGroups : visibleGroups).map((group) => (
                  <span key={group.uuid} className="entity-info-v2__group-pill">
                    {group.avatar ? (
                      <img src={group.avatar} alt="" />
                    ) : (
                      <em>{getInitials(group.name)}</em>
                    )}
                    {group.name}
                  </span>
                ))}
                {hiddenGroups.length > 0 && !showAllGroups && (
                  <button
                    type="button"
                    className="entity-info-v2__group-more"
                    onClick={() => setShowAllGroups(true)}
                  >
                    +{hiddenGroups.length}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="entity-info-v2__detail-block">
            <span>Tags</span>
            {loading ? (
              <div className="entity-info-v2__tags">
                <Skeleton width={60} height={26} borderRadius={999} />
              </div>
            ) : (
              <div className="entity-info-v2__tags">
                {tags?.length
                  ? tags.map((tag) => (
                      <span key={tag.id} className="entity-info-v2__tag">
                        {tag.name}
                      </span>
                    ))
                  : <em className="entity-info-v2__muted">—</em>}
              </div>
            )}
          </div>
        </div>
      </div>

      {kpiCards.length > 0 && (
        <div className={`entity-info-v2__kpis entity-info-v2__kpis--${kpiCards.length}`}>
          {kpiCards.map((card) => {
            const Icon = card.icon;
            return (
              <article key={card.key} className="entity-info-v2__kpi-card">
                <div className={`entity-info-v2__kpi-icon entity-info-v2__kpi-icon--${card.tone}`}>
                  <Icon />
                </div>
                <div className="entity-info-v2__kpi-body">
                  <span className="entity-info-v2__kpi-label">{card.label}</span>
                  {loading ? (
                    <Skeleton width={48} height={28} />
                  ) : (
                    <strong className="entity-info-v2__kpi-value">{card.value}</strong>
                  )}
                  <small>{card.subtitle}</small>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <div className="entity-info-v2__tabs-panel">
        <div className="entity-info-v2__tabs-bar">
          <div className="entity-info-v2__tabs">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`entity-info-v2__tab ${activeTab === tab.key ? "is-active" : ""}`}
                onClick={() => {
                  scrollAppToTop();
                  onTabChange(tab.key);
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {tabActions ? <div className="entity-info-v2__tab-actions">{tabActions}</div> : null}
        </div>

        <div className="entity-info-v2__content">{children}</div>
      </div>
    </div>
  );
};

export default EntityInfoV2View;
